import { useWorkspaceTree } from "@/api/fileRecords";
import { useWorkspaceFlowSessions } from "@/api/flow_sessions";
import type { Canvas, FileRecord, InboxItem } from "@cohesive/api-client";
import { useMemo } from "react";
import type { HomeTab } from "../utils/homeBrowserTypes";

export interface WorkspaceBrowserData {
  /** The workspace tree — folders, canvases and files in one list, unscoped
   *  by folder; the browser scopes per level. */
  tree: FileRecord[];
  /** The canvases in the tree, read off their rows. */
  canvases: Canvas[];
  chats: InboxItem[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Data for the workspace browser's typed tabs (Files / Chats): the
 * workspace's FULL resource sets including folders, uncapped — unlike
 * `useHomeBrowserData`, which feeds the recents-style Starred tab.
 * Only the active tab's queries are enabled; caches persist across tabs.
 */
export function useWorkspaceBrowserData(
  tab: HomeTab,
  workspaceId: string,
): WorkspaceBrowserData {
  const isChats = tab === "chats";

  // The tree loads on every tab: it also feeds folder cascade-delete, the
  // move dialogs, and unique-name generation page-wide.
  const treeQuery = useWorkspaceTree(workspaceId);
  const chatsQuery = useWorkspaceFlowSessions(workspaceId, {
    enabled: isChats,
  });

  const tree = treeQuery.data ?? [];
  const canvases = useMemo(
    () =>
      tree.flatMap((record) =>
        record.kind === "canvas" && record.canvas ? [record.canvas] : [],
      ),
    [tree],
  );

  // Client-side safety filter mirroring the old detail page: only chats that
  // actually belong to this workspace (guards against stale cache entries).
  const chats = useMemo(
    () =>
      (chatsQuery.data ?? []).filter(
        (item) => item.flow_session?.workspace_id === workspaceId,
      ),
    [chatsQuery.data, workspaceId],
  );

  const activeQueries = isChats ? [chatsQuery] : [treeQuery];

  return {
    tree,
    canvases,
    chats,
    isPending:
      activeQueries.length > 0 && activeQueries.every((q) => q.isPending),
    isError: activeQueries.some((q) => q.isError),
    refetch: () => activeQueries.forEach((q) => void q.refetch()),
  };
}
